"use client";

import { useState } from "react";
import { Check, AlertCircle, Loader2, Clock, ChevronDown } from "lucide-react";
import { humanizeTool, formatDuration } from "./humanizeTool";

export type TimelineStep = {
  stepIndex: number;
  tool: string;
  status: "running" | "done" | "error" | "pending";
  startedAt: number;
  durationMs?: number;
  inputPreview?: Record<string, unknown> | null;
  summary?: string | null;
};

type Props = {
  statusText?: string | null;
  timeline: TimelineStep[];
};

/**
 * Timeline visual estilo Claude Code. Avatar circular con pulse, header con
 * 3 dots animados, lista de pasos con status icon + duración + expandible.
 */
export default function CaonaboTimeline({ statusText, timeline }: Props) {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const showTimeline = timeline.length > 0;

  return (
    <div className="flex justify-start gap-2 caonabo-fade-in">
      {/* Avatar pulsante terracotta */}
      <span
        className="caonabo-avatar-pulse inline-flex items-center justify-center rounded-xl shrink-0"
        style={{
          width: 28,
          height: 28,
          background: "linear-gradient(135deg, #8B3A2E 0%, #6B2820 100%)",
          color: "white",
        }}
      >
        <Loader2 size={14} className="animate-spin" />
      </span>

      {/* Bloque de trabajo */}
      <div
        className="px-3 py-2 rounded-2xl rounded-bl-sm flex-1 min-w-0"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Header: 3 dots + status text */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {[0, 0.18, 0.36].map((d, i) => (
              <span
                key={i}
                className="caonabo-dot inline-block w-1.5 h-1.5 rounded-full"
                style={{
                  background: "#D77F70",
                  animationDelay: `${d}s`,
                }}
              />
            ))}
          </div>
          <span className="text-[11px] text-text-muted font-medium">
            {statusText ?? "Pensando…"}
          </span>
        </div>

        {/* Lista de pasos */}
        {showTimeline && (
          <ul className="mt-2 space-y-1">
            {timeline.map((step) => {
              const isExpanded = expandedStep === step.stepIndex;
              const label = humanizeTool(step.tool);
              return (
                <li key={step.stepIndex} className="text-[11.5px]">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedStep(isExpanded ? null : step.stepIndex)
                    }
                    className="w-full text-left flex items-start gap-1.5 hover:bg-white/5 rounded px-1 py-0.5"
                  >
                    <StepIcon status={step.status} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`truncate ${
                            step.status === "running"
                              ? "text-text font-medium"
                              : step.status === "error"
                                ? "text-red-400"
                                : step.status === "pending"
                                  ? "text-amber-400"
                                  : "text-text-muted"
                          }`}
                        >
                          {label}
                        </span>
                        {typeof step.durationMs === "number" && (
                          <span className="text-[9.5px] text-text-muted/70 tabular-nums">
                            {formatDuration(step.durationMs)}
                          </span>
                        )}
                      </div>
                      {step.summary && (
                        <div className="text-[10.5px] text-text-muted truncate">
                          {step.summary}
                        </div>
                      )}
                    </div>
                    {step.inputPreview && Object.keys(step.inputPreview).length > 0 && (
                      <ChevronDown
                        size={10}
                        className={`text-text-muted shrink-0 mt-0.5 transition-transform ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </button>

                  {/* Preview del input expandido */}
                  {isExpanded && step.inputPreview && (
                    <div
                      className="ml-5 mt-0.5 px-2 py-1.5 rounded-md text-[10px] font-mono caonabo-fade-in"
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        color: "#D77F70",
                        maxHeight: 120,
                        overflow: "auto",
                      }}
                    >
                      {Object.entries(step.inputPreview).map(([k, v]) => (
                        <div key={k} className="truncate">
                          <span className="opacity-70">{k}:</span>{" "}
                          <span>{JSON.stringify(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function StepIcon({ status }: { status: TimelineStep["status"] }) {
  if (status === "done")
    return <Check size={11} className="text-emerald-400 shrink-0 mt-0.5" />;
  if (status === "error")
    return <AlertCircle size={11} className="text-red-400 shrink-0 mt-0.5" />;
  if (status === "pending")
    return <Clock size={11} className="text-amber-400 shrink-0 mt-0.5" />;
  return <Loader2 size={11} className="text-[#D77F70] animate-spin shrink-0 mt-0.5" />;
}
